import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import booleanParser from 'boolean-parser'

import { ConditionEvaluatorContext } from '../contexts/ConditionEvaluatorContext'
import { withApi } from './ApiProvider'
import { USER_OPTIONS_KEY, ACCOUNT_OPTIONS_KEY, ACCOUNT_STORE_KEY } from '../helpers/Constants.js'
import AppStateContext from '../contexts/AppStateContext'

export const withConditionEvaluator = ComponentToWrap => {
  const WrappedComponent = (props) => {
    return (
      <ConditionEvaluatorContext.Consumer>
        {value => (
          <ComponentToWrap {...props} conditionEvaluatorApi={value} />
        )}
      </ConditionEvaluatorContext.Consumer>
    )
  }

  WrappedComponent.displayName = `withConditionEvaluator(${ComponentToWrap.displayName || ComponentToWrap.name})`
  return WrappedComponent
}

const ConditionEvaluatorProviderComponent = (props) => {
  const { children } = props

  const { storeGet } = useContext(AppStateContext)

  const evalUserOption = (optionName) => {
    const userOptions = storeGet(USER_OPTIONS_KEY) || []
    return userOptions.includes(optionName)
  }

  const evalAccountOption = (optionName) => {
    const accountOptions = storeGet(ACCOUNT_OPTIONS_KEY) || []
    return accountOptions.includes(optionName)
  }

  const evalAccountProperty = (propertyName) => {
    const account = storeGet(ACCOUNT_STORE_KEY) || {}
    return account.hasOwnProperty(propertyName) ? account[propertyName] : false
  }

  const evalConditionToken = (t) => {
    const matches = t.trim().match(/^NOT\s+(.*)$/)

    if (matches) {
      return !evalSingleCondition(matches[1])
    } else {
      return evalSingleCondition(t)
    }
  }

  const evalSingleCondition = (condition) => {
    const matches = condition.match(/(uo|co|c):(.*)/)

    let result = false
    switch (matches[1]) {
      case 'uo':
        result = evalUserOption(matches[2])
        break

      case 'co':
        result = evalAccountOption(matches[2])
        break

      case 'c':
        result = evalAccountProperty(matches[2])
        break
    }
    return result
  }

  const evalCondition = (condition) => {
    //  it returns an OR array of AND combination
    const booleanTokens = booleanParser.parseBooleanQuery(condition)

    const result = booleanTokens.reduce((currentValue, item) => {
      return currentValue || item.reduce((currentAndValue, conditionToken) => {
        return currentAndValue && evalConditionToken(conditionToken)
      }, true)
    }, false)

    return result
  }

  const api = {
    evalCondition: evalCondition
  }

  return (
    <ConditionEvaluatorContext.Provider value={api}>
      {children}
    </ConditionEvaluatorContext.Provider>
  )
}

ConditionEvaluatorProviderComponent.defaultProps = {
  children: null
}

ConditionEvaluatorProviderComponent.propTypes = {
  children: PropTypes.node,
  apiWrapper: PropTypes.func.isRequired
}

export const ConditionEvaluatorProvider = withApi(ConditionEvaluatorProviderComponent)
